import { z } from 'zod';
import { payCycleLikeSchema } from './pay-cycle';
import { coachUserSchema } from '../coach';

export const transferWebhookEventKind = z.enum(['TRANSFER_PROCESSED', 'TRANSFER_FAILED']);
export const paymentAccountTypeKind = z.enum(['ACH', 'DOMESTIC_WIRE', 'CHEQUE', 'INTERNATIONAL_WIRE']);
export const transferStatusKind = z.enum(['PROCESSING', 'SCHEDULED', 'PENDING_APPROVAL', 'FAILED', 'PROCESSED']);
export const cancellationReasonKind = z.enum([
  'USER_CANCELLED',
  'INSUFFICIENT_FUNDS',
  'APPROVAL_DENIED',
  'BLOCKED_BY_POSITIVE_PAY',
]);

export const paymentAccountTypeKindToLabel = {
  [paymentAccountTypeKind.enum.ACH]: 'ACH',
  [paymentAccountTypeKind.enum.DOMESTIC_WIRE]: 'Domestic Wire',
  [paymentAccountTypeKind.enum.CHEQUE]: 'Cheque',
  [paymentAccountTypeKind.enum.INTERNATIONAL_WIRE]: 'International Wire',
};

export const accountClassEnum = z.enum(['BUSINESS', 'PERSONAL']);

export type AccountClass = z.infer<typeof accountClassEnum>;

export const accountClassToLabel = {
  [accountClassEnum.enum.BUSINESS]: 'Business',
  [accountClassEnum.enum.PERSONAL]: 'Personal',
};

export const accountTypeEnum = z.enum(['CHECKING', 'SAVING']);

export type AccountType = z.infer<typeof accountTypeEnum>;

export const accountTypeToLabel = {
  [accountTypeEnum.enum.CHECKING]: 'Checking',
  [accountTypeEnum.enum.SAVING]: 'Saving',
};

export type BrexPagination<T> = {
  next_cursor: string;
  items: T[];
};

export const vendorAddressSchema = z
  .object({
    line1: z.string().trim().optional().describe('Address line 1, no PO Box.'),
    line2: z.string().trim().optional().describe('Address line 2 (e.g., apartment, suite, unit, or building).'),
    city: z.string().trim().optional().describe('City, district, suburb, town, or village.'),
    state: z
      .string()
      .trim()
      .optional()
      .describe(
        'For US-addresses the 2-letter State abbreviation. For international-addresses the county, province, or region.',
      ),
    country: z.string().trim().optional().describe('Two-letter country code (ISO 3166-1 alpha-2).'),
    postal_code: z.string().trim().optional().describe('ZIP or postal code.'),
    phone_number: z.string().trim().optional().describe('Phone number.'),
  })
  .describe('Company business address (must be in the US; no PO box or virtual/forwarding addresses allowed).');

export const brexVendorACHSchema = z.object({
  type: z.literal(paymentAccountTypeKind.enum.ACH),
  payment_instrument_id: z.string().trim(),
  routing_number: z.string().trim(),
  account_number: z.string().trim(),
  account_type: accountTypeEnum.nullable(),
  account_class: accountClassEnum.nullable(),
});

export type BrexVendorACH = z.infer<typeof brexVendorACHSchema>;

export const brexVendorDomesticWireSchema = z.object({
  type: z.literal(paymentAccountTypeKind.enum.DOMESTIC_WIRE),
  payment_instrument_id: z.string().trim(),
  routing_number: z.string().trim(),
  account_number: z.string().trim(),
  address: vendorAddressSchema,
});

export const vendorChequeSchema = z.object({
  type: z.literal(paymentAccountTypeKind.enum.CHEQUE),
  payment_instrument_id: z.string().trim(),
  mailing_address: vendorAddressSchema,
  recipient_name: z.string().trim(),
});

export const brexVendorInternationalWireSchema = z.object({
  type: z.literal(paymentAccountTypeKind.enum.INTERNATIONAL_WIRE),
  payment_instrument_id: z.string().trim(),
  swift_code: z.string().trim().describe("Counterparty's SWIFT code"),
  iban: z.string().trim().describe("Counterparty's international bank account number"),
  beneficiary_bank_name: z.string().trim().describe("Name of counterparty's bank").optional(),
  address: vendorAddressSchema,
});

export const brexPaymentAccounts = z.object({
  details: z.discriminatedUnion('type', [
    brexVendorACHSchema,
    brexVendorDomesticWireSchema,
    vendorChequeSchema,
    brexVendorInternationalWireSchema,
  ]),
});

export type BrexPaymentAccounts = z.infer<typeof brexPaymentAccounts>;

export const brexVendorSchema = z.object({
  id: z.string().describe('vendor id'),
  company_name: z.string().trim().describe('Teacher name').optional(),
  email: z.string().trim().email().optional(),
  phone: z.string().trim().optional(),
  payment_accounts: z.array(brexPaymentAccounts).optional(),
});

export type BrexVendor = z.infer<typeof brexVendorSchema>;

export const brexErrorSchema = z.object({
  type: z.literal('BAD_REQUEST'),
  message: z.string(),
});

export type BrexError = z.infer<typeof brexErrorSchema>;

export const brexVendorCompanyNameAlreadyExists = z.object({
  type: z.literal('BAD_REQUEST'),
  message: z
    .literal(
      'Vendor already exists. Please update the name of this vendor or delete the previously created vendor with the same name',
    )
    .or(z.literal("Can't update vendor name to an existing vendor name")),
});

export const brexPaymentInstrumentAlreadyExists = z.object({
  type: z.literal('BAD_REQUEST'),
  message: z.literal(
    'A payment instrument already exists with conflicting values. Please try to update the existing one instead',
  ),
});

export type BrexPaymentInstrumentAlreadyExists = z.infer<typeof brexPaymentInstrumentAlreadyExists>;

export type BrexVendorCompanyNameAlreadyExists = z.infer<typeof brexVendorCompanyNameAlreadyExists>;

export const createVendorACHSchema = brexVendorACHSchema.omit({
  payment_instrument_id: true,
});

export const createVendorDomesticWireSchema = brexVendorDomesticWireSchema.omit({
  payment_instrument_id: true,
});

export const createBrexVendorSchema = z.object({
  company_name: z.string().trim().describe('Teacher name'),
  email: z.string().email().trim().optional(),
  phone: z.string().trim().optional(),
  payment_accounts: z.array(
    z.object({
      details: z.discriminatedUnion('type', [createVendorACHSchema, createVendorDomesticWireSchema]),
    }),
  ),
});

export type CreateBrexVendor = z.infer<typeof createBrexVendorSchema>;

export const updateBrexVendorSchema = z.object({
  company_name: z.string().trim().describe('Teacher name').optional(),
  email: z.string().email().trim().optional(),
  phone: z.string().trim().optional(),
  payment_accounts: z.array(
    z.object({
      details: z.discriminatedUnion('type', [createVendorACHSchema, createVendorDomesticWireSchema]),
    }),
  ),
});

export type UpdateBrexVendor = z.infer<typeof updateBrexVendorSchema>;

export const brexTransferSchema = z.object({
  id: z.string(),
  counterparty: z
    .object({
      type: z.literal('VENDOR'),
      payment_instrument_id: z.string(),
      id: z.string(),
      routing_number: z.string(),
      account_number: z.string(),
    })
    .optional(),
  description: z.string().optional(),
  payment_type: paymentAccountTypeKind,
  amount: z.object({
    amount: z.number(),
    currency: z.string(),
  }),
  process_date: z.string().optional(),
  originating_account: z.object({
    type: z.literal('BREX_CASH'),
    id: z.string(),
  }),
  status: transferStatusKind,
  cancellation_reason: cancellationReasonKind.optional(),
  estimated_delivery_date: z.string().optional(),
  creator_user_id: z.string().optional(),
  created_at: z.string().optional(),
  display_name: z.string().optional(),
  external_memo: z.string().optional(),
  is_ppro_enabled: z.boolean().optional(),
});

export type BrexTransfer = z.infer<typeof brexTransferSchema>;

export const createBrexTransferSchema = z.object({
  counterparty: z.object({
    type: z.literal('VENDOR'),
    payment_instrument_id: z.string(),
  }),
  amount: z.object({
    amount: z.number().positive(),
    currency: z.literal('USD'),
  }),
  description: z.string().trim(),
  external_memo: z.string().trim(),
  originating_account: z.object({
    type: z.literal('BREX_CASH'),
    id: z.string(),
  }),
});

export type CreateBrexTransfer = z.infer<typeof createBrexTransferSchema>;

export const createBrexTransferFromData = z.object({
  totalAmount: z.number().positive(),
  paymentInstrumentId: z.string(),
  payCycle: z.lazy(() => payCycleLikeSchema),
  coach: z.lazy(() => coachUserSchema),
});

export type CreateBrexTransferFromData = z.infer<typeof createBrexTransferFromData>;

export const brexTransferWebhook = z.object({
  company_id: z.string(),
  event_type: transferWebhookEventKind,
  payment_type: paymentAccountTypeKind,
  transfer_id: z.string(),
  return_for_id: z.string().optional(),
});

export type BrexTransferWebhook = z.infer<typeof brexTransferWebhook>;
